import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { toast } from "sonner";
import {
  ArrowLeft, RotateCcw, Maximize2, Minimize2, Minus, Plus, Trash2, CheckCircle2,
  Circle, Lightbulb, Target, ClipboardList, Sparkles, StickyNote, Trophy, Loader2, ListChecks, BookOpen,
} from "lucide-react";
import { api } from "@/lib/api";
import Terminal from "@/components/Terminal";
import AiTutor from "@/components/AiTutor";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// render inline **bold** and `code` segments in a step string
function renderStep(text) {
  return String(text).split(/(\*\*.+?\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`"))
      return <code key={i} className="px-1 py-0.5 rounded bg-muted font-mono text-[11px] text-foreground">{part.slice(1, -1)}</code>;
    return <span key={i}>{part}</span>;
  });
}


export default function LabWorkspace() {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const termRef = useRef(null);

  const [lab, setLab] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [cwd, setCwd] = useState("/");
  const [fontSize, setFontSize] = useState(14);
  const [fullscreen, setFullscreen] = useState(false);
  const [report, setReport] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(null);
  const [policy, setPolicy] = useState({ enforce: false, restrict_copy: false });
  const validateTimer = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [labRes, startRes, noteRes, polRes] = await Promise.all([
          api.get(`/labs/${labId}`),
          api.post(`/labs/${labId}/start`),
          api.get(`/notes/${labId}`),
          api.get(`/lab-policy/${labId}`),
        ]);
        if (!active) return;
        setLab(labRes.data.lab);
        setSessionId(startRes.data.session_id);
        setCwd(startRes.data.cwd);
        setNotes(noteRes.data.content || "");
        setPolicy(polRes.data || { enforce: false, restrict_copy: false });
      } catch (e) {
        toast.error("Could not open the lab.");
      }
    })();
    return () => { active = false; };
  }, [labId]);

  const onPasteBlocked = useCallback((method) => {
    toast.warning("Copy & paste is disabled — type the command manually to build real hands-on skill.");
    if (sessionId) api.post(`/sessions/${sessionId}/paste-blocked`, { method }).catch(() => {});
  }, [sessionId]);

  const guardCopy = useCallback((e) => {
    if (policy.restrict_copy) { e.preventDefault(); }
  }, [policy.restrict_copy]);

  const validate = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.post(`/sessions/${sessionId}/validate`);
      setReport(data.report);
      if (data.progress?.newly_completed) {
        setDone({ ...data.report, progress: data.progress });
        toast.success(`Lab complete! +${lab?.xp} XP`);
      }
    } catch (e) { /* ignore */ }
  }, [sessionId, lab]);

  const onCommandDone = useCallback(() => {
    clearTimeout(validateTimer.current);
    validateTimer.current = setTimeout(validate, 500);
  }, [validate]);

  const reset = async () => {
    if (!sessionId) return;
    await api.post(`/sessions/${sessionId}/reset`);
    termRef.current?.reset();
    setReport(null);
    setCwd("/");
    toast.info("Lab reset to initial state.");
  };

  const changeFont = (delta) => {
    setFontSize((fs) => {
      const nf = Math.min(22, Math.max(10, fs + delta));
      termRef.current?.setFontSize(nf);
      return nf;
    });
  };

  const revealHint = async (taskId) => {
    if (revealed[taskId]) return;
    setRevealed((r) => ({ ...r, [taskId]: true }));
    try { await api.post(`/sessions/${sessionId}/hint`); } catch (e) {}
  };

  const saveNotes = async () => {
    try { await api.put("/notes", { lab_id: labId, content: notes }); toast.success("Notes saved"); } catch (e) {}
  };

  useEffect(() => {
    termRef.current?.refit();
  }, [fullscreen]);

  if (user?.must_change_password) return <Navigate to="/change-password" replace />;

  if (!lab || !sessionId) {
    return (
      <div className="h-screen grid place-items-center bg-background text-muted-foreground">
        <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Provisioning your lab environment…</div>
      </div>
    );
  }

  const taskStatus = (tid) => report?.results.find((r) => r.id === tid)?.passed;
  const completed = report?.completed || 0;
  const total = lab.tasks.length;
  const pct = report?.score || 0;
  const currentTask = lab.tasks.find((t) => !taskStatus(t.id));

  const TerminalHeader = (
    <div className="flex items-center justify-between px-3 py-2 bg-[#0b0b0d] border-b border-white/10">
      <div className="flex items-center gap-2">
        <div className="flex gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-500/80" />
          <span className="h-3 w-3 rounded-full bg-yellow-500/80" />
          <span className="h-3 w-3 rounded-full bg-green-500/80" />
        </div>
        <span className="ml-2 font-mono text-xs text-white/50">student@lab: ~{cwd === "/" ? "" : cwd}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => changeFont(-1)} data-testid="font-decrease"><Minus className="h-3.5 w-3.5" /></Button>
        <span className="font-mono text-xs text-white/50 w-6 text-center">{fontSize}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => changeFont(1)} data-testid="font-increase"><Plus className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => termRef.current?.clear()} title="Clear" data-testid="term-clear"><Trash2 className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-white/60 hover:text-white hover:bg-white/10" onClick={() => setFullscreen((f) => !f)} data-testid="fullscreen-toggle">
          {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/curriculum")} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <Badge variant="secondary" className="text-[10px] uppercase tracking-widest shrink-0">{lab.difficulty}</Badge>
          <h1 className="font-display font-bold text-base tracking-tight truncate">{lab.title}</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden sm:flex items-center gap-2 mr-2">
            <span className="text-xs text-muted-foreground">{completed}/{total} tasks</span>
            <Progress value={pct} className="h-1.5 w-24" />
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="reset-lab-btn"><RotateCcw className="h-3.5 w-3.5" /> Reset</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset this lab?</AlertDialogTitle>
                <AlertDialogDescription>This wipes your sandbox and restores the lab's initial state. Your saved progress and XP are not affected.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={reset} data-testid="reset-confirm">Reset environment</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </header>

      {/* Fullscreen terminal overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black">
          {TerminalHeader}
          <div className="flex-1 min-h-0"><Terminal ref={termRef} sessionId={sessionId} initialCwd={cwd} fontSize={fontSize} onCwdChange={setCwd} onCommandDone={onCommandDone} enforceManual={policy.enforce} onPasteBlocked={onPasteBlocked} /></div>
        </div>
      )}

      {/* 3-panel workspace */}
      <PanelGroup direction="horizontal" className="flex-1 min-h-0">
        {/* Lesson panel */}
        <Panel defaultSize={28} minSize={20} className="min-w-0">
          <Tabs defaultValue="lesson" className="h-full flex flex-col">
            <TabsList className="mx-3 mt-3 grid grid-cols-4">
              <TabsTrigger value="lesson" data-testid="tab-lesson"><ClipboardList className="h-4 w-4 mr-1.5" />Lab</TabsTrigger>
              <TabsTrigger value="cheatsheet" data-testid="tab-cheatsheet"><BookOpen className="h-4 w-4 mr-1.5" />Cheatsheet</TabsTrigger>
              <TabsTrigger value="tutor" data-testid="tab-tutor"><Sparkles className="h-4 w-4 mr-1.5" />Tutor</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="h-4 w-4 mr-1.5" />Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="lesson" className={`flex-1 overflow-y-auto px-5 py-4 mt-0 space-y-5 ${policy.restrict_copy ? "select-none" : ""}`} onCopy={guardCopy} onCut={guardCopy} onContextMenu={guardCopy}>
              <div>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground mb-1"><Target className="h-3.5 w-3.5" /> Scenario</div>
                <p className="text-sm leading-relaxed">{lab.scenario}</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Objectives</div>
                <ul className="space-y-1.5">
                  {lab.objectives.map((o, i) => (<li key={i} className="text-sm flex gap-2"><span className="text-primary">›</span>{o}</li>))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Commands you'll use</div>
                <div className="flex flex-wrap gap-1.5">
                  {lab.commands.map((c) => (<code key={c} className="font-mono text-xs bg-secondary px-2 py-1 rounded">{c}</code>))}
                </div>
              </div>
              {lab.common_mistakes?.length > 0 && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="text-xs uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-1.5">Common mistakes</div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {lab.common_mistakes.map((m, i) => (<li key={i}>• {m}</li>))}
                  </ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="cheatsheet" className={`flex-1 overflow-y-auto px-4 py-4 mt-0 space-y-2.5 ${policy.restrict_copy ? "select-none" : ""}`} onCopy={guardCopy} onCut={guardCopy} onContextMenu={guardCopy} data-testid="cheatsheet-panel">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground mb-1"><BookOpen className="h-3.5 w-3.5" /> Command reference for this lab</div>
              {(lab.cheatsheet || []).map((c) => (
                <div key={c.name} className="rounded-lg border border-border bg-card p-3" data-testid={`cheatsheet-${c.name}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-sm font-semibold text-primary">{c.name}</code>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{c.category}</span>
                  </div>
                  <div className="mt-1.5 font-mono text-[11px] text-muted-foreground bg-secondary/60 rounded px-2 py-1">{c.syntax}</div>
                  {c.desc && <p className="text-xs mt-1.5 leading-relaxed">{c.desc}</p>}
                  {c.example && (
                    <div className="mt-1.5 text-[11px] flex items-start gap-1.5">
                      <span className="text-muted-foreground shrink-0">e.g.</span>
                      <code className="font-mono text-emerald-600 dark:text-emerald-400">{c.example}</code>
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="tutor" className="flex-1 mt-0 min-h-0">
              <AiTutor labId={labId} taskContext={currentTask?.title} />
            </TabsContent>

            <TabsContent value="notes" className="flex-1 mt-0 px-4 py-4 flex flex-col">
              <Textarea data-testid="notes-area" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Jot down commands, syntax, and reminders…" className="flex-1 resize-none font-mono text-sm" />
              <Button size="sm" className="mt-3 w-fit" onClick={saveNotes} data-testid="save-notes">Save notes</Button>
            </TabsContent>
          </Tabs>
        </Panel>

        <PanelResizeHandle className="w-1.5 bg-border hover:bg-primary/40 transition-colors" />

        {/* Terminal panel */}
        <Panel defaultSize={44} minSize={30} className="min-w-0">
          <div className="h-full flex flex-col">
            {TerminalHeader}
            <div className="flex-1 min-h-0">
              {!fullscreen && <Terminal ref={termRef} sessionId={sessionId} initialCwd={cwd} fontSize={fontSize} onCwdChange={setCwd} onCommandDone={onCommandDone} enforceManual={policy.enforce} onPasteBlocked={onPasteBlocked} />}
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="w-1.5 bg-border hover:bg-primary/40 transition-colors" />

        {/* Validation panel */}
        <Panel defaultSize={28} minSize={20} className="min-w-0">
          <div className="h-full flex flex-col">
            <div className="px-5 py-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-sm">Validation & Progress</h2>
                <Badge variant={report?.all_passed ? "default" : "secondary"} className="text-xs">{pct}%</Badge>
              </div>
              <Progress value={pct} className="h-2 mt-3" />
              <div className="text-xs text-muted-foreground mt-2">{completed} of {total} tasks complete</div>
            </div>
            <div className={`flex-1 overflow-y-auto px-4 py-4 space-y-2 ${policy.restrict_copy ? "select-none" : ""}`} onCopy={guardCopy} onCut={guardCopy} onContextMenu={guardCopy}>
              {lab.tasks.map((t, i) => {
                const passed = taskStatus(t.id);
                return (
                  <div key={t.id} className={`rounded-md border p-3 transition-colors ${passed ? "border-success/40 bg-success/5" : "border-border"}`} data-testid={`task-${t.id}`}>
                    <div className="flex items-start gap-2.5">
                      {passed ? <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" data-testid={`task-${t.id}-passed`} /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <div className="text-sm font-medium leading-snug">{i + 1}. {t.title}</div>
                        {(t.steps || []).length > 0 && (
                          <div className="mt-2 rounded-md border border-primary/25 bg-primary/5 p-2.5" data-testid={`task-steps-${t.id}`}>
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-primary mb-1.5">
                              <ListChecks className="h-3.5 w-3.5" /> Steps to complete
                            </div>
                            <ol className="space-y-1">
                              {t.steps.map((s, si) => (
                                <li key={si} className="flex gap-2 text-xs leading-relaxed">
                                  <span className="shrink-0 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center mt-0.5">{si + 1}</span>
                                  <span>{renderStep(s)}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-border">
              <Button className="w-full gap-2" onClick={validate} data-testid="check-work-btn"><CheckCircle2 className="h-4 w-4" /> Check my work</Button>
            </div>
          </div>
        </Panel>
      </PanelGroup>

      {/* Completion dialog */}
      <Dialog open={!!done} onOpenChange={(o) => !o && setDone(null)}>
        <DialogContent className="sm:max-w-md" data-testid="completion-dialog">
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-success/15 grid place-items-center mb-2">
              <Trophy className="h-7 w-7 text-success" />
            </div>
            <DialogTitle className="text-center font-display text-2xl">Lab complete!</DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-1">
            <p className="text-muted-foreground text-sm">You finished <span className="font-semibold text-foreground">{lab.title}</span></p>
            <div className="flex justify-center gap-6 mt-4">
              <div><div className="font-display font-bold text-2xl text-primary">+{lab.xp}</div><div className="text-xs text-muted-foreground">XP earned</div></div>
              <div><div className="font-display font-bold text-2xl">{done?.score}%</div><div className="text-xs text-muted-foreground">Score</div></div>
              <div><div className="font-display font-bold text-2xl">{done?.progress?.streak}🔥</div><div className="text-xs text-muted-foreground">Streak</div></div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setDone(null)} data-testid="stay-btn">Keep practicing</Button>
            {lab.next ? (
              <Button className="flex-1" data-testid="next-lab-btn" onClick={() => { setDone(null); navigate(`/lab/${lab.next}`); }}>Next lab</Button>
            ) : (
              <Button className="flex-1" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
